import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, CheckCircle, Wifi, ArrowRight, Trophy, Newspaper, Users, Hourglass, Swords, Clapperboard, Play, Download } from 'lucide-react';
import { getFeaturedGuide } from '../data/guides';
import VideoModal from '../components/VideoModal';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import { getCoverUrl } from '../utils/newsImage';

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Frostbound',
  url: 'https://www.frostbound.org',
  logo: 'https://www.frostbound.org/favicon.png',
  sameAs: [],
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Frostbound',
  url: 'https://www.frostbound.org',
  description: 'Servidor privado WoW WotLK 3.3.5a en español con Warband, PvE, PvP y bots IA.',
};

// Intenta cargar desde dominio propio primero; si no existe, cae al CDN externo.
// Para eliminar la dependencia externa: guardar el logo en frontend/public/logo.png
const LOGO_LOCAL = '/logo.png';
const LOGO_FALLBACK = 'https://customer-assets.emergentagent.com/job_frostbound-wow/artifacts/lcob9xzr_frostbound_logo_final.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;
const REALMLIST = 'set realmlist logon.frostbound.org';
const DISCORD_URL = 'https://discord.com/invite/RwpMfRdzex';

const CATEGORY_COLORS = {
  PARCHE: { bg: 'rgba(0,120,255,0.18)', border: 'rgba(0,120,255,0.45)', text: '#69CCF0' },
  EVENTO: { bg: 'rgba(255,140,0,0.15)', border: 'rgba(255,140,0,0.45)', text: '#FFAA44' },
  PVP: { bg: 'rgba(200,30,30,0.15)', border: 'rgba(200,30,30,0.45)', text: '#FF6666' },
  AVISO: { bg: 'rgba(160,50,255,0.15)', border: 'rgba(160,50,255,0.45)', text: '#B080FF' },
};

const CLASS_ICON_SLUG = {
  Guerrero: 'warrior',
  Paladín: 'paladin',
  Cazador: 'hunter',
  Pícaro: 'rogue',
  Sacerdote: 'priest',
  'Caballero de la Muerte': 'deathknight',
  Chamán: 'shaman',
  Mago: 'mage',
  Brujo: 'warlock',
  Druida: 'druid',
};

function ClassIcon({ className, size = 16 }) {
  const slug = CLASS_ICON_SLUG[className];
  if (!slug) return null;
  return (
    <img
      src={`https://wow.zamimg.com/images/wow/icons/small/classicon_${slug}.jpg`}
      alt={className}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      style={{ borderRadius: 3, verticalAlign: 'middle', marginRight: 6, flexShrink: 0 }}
    />
  );
}

function GearBar({ score }) {
  const max = 284;
  const pct = Math.min(100, (score / max) * 100);
  return (
    <div className="gs-bar-wrap">
      <div className="gs-bar-track">
        <div className="gs-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="gs-score">{score.toLocaleString()}</span>
    </div>
  );
}

const IMG_SRC_RE = /<img[^>]+src=["']([^"']+)["']/i;
function extractFirstImage(html) {
  if (!html) return null;
  const m = html.match(IMG_SRC_RE);
  if (!m) return null;
  const src = m[1];
  if (src.startsWith('/uploads/')) return `${BACKEND_URL}${src}`;
  if (src.includes('/uploads/')) return `${BACKEND_URL}/uploads/${src.split('/uploads/')[1]}`;
  return src;
}

export default function HeroPage() {
  const [characters, setCharacters] = useState([]);
  const [news, setNews] = useState([]);
  const [copied, setCopied] = useState(false);
  const [featuredVideoOpen, setFeaturedVideoOpen] = useState(false);
  const [online, setOnline] = useState(null);
  const [activeNewsIdx, setActiveNewsIdx] = useState(0);
  const [isTouch, setIsTouch] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/armory/top?limit=5`).then(({ data }) => setCharacters(data)).catch(() => { });
    axios.get(`${BACKEND_URL}/api/news?limit=8`).then(({ data }) => setNews(data)).catch(() => { });
    axios.get(`${BACKEND_URL}/api/status/online`).then(({ data }) => setOnline(data.online)).catch(() => { });
  }, []);

  // Detect touch / hover-less devices once (mobile, tablets)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      setIsTouch(window.matchMedia('(hover: none)').matches);
    }
  }, []);

  const copyRealmlist = () => {
    navigator.clipboard.writeText(REALMLIST).catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Handle rail item tap on touch devices: ALWAYS preview only, never navigate.
  // (Hero is the navigation surface on touch.)
  const handleRailItemClick = (e, i) => {
    if (isTouch) {
      e.preventDefault();
      if (i !== activeNewsIdx) {
        setActiveNewsIdx(i);
      }
    }
  };

  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Servidor WoW 3.3.5a en español con Warband y WotLK personalizada"
        description="Juega en Frostbound, servidor WoW WotLK 3.3.5a con Warband, progresión acelerada, PvE, PvP y bots IA. Comunidad en español. Descarga gratis."
        canonical="/"
        jsonLd={[ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]}
      />
      <Navbar />

      {/* ── HERO CARD ── */}
      <section className="home-hero-card anim-fade-up" style={{ animationDelay: '0.4s' }}>
        <h1 className="sr-only">Frostbound — Servidor WoW 3.3.5a en español con Warband y WotLK personalizada</h1>
        <div className="home-hero-card-inner">
          <div className="home-hero-card-body">
            <div className="home-hero-card-left">
              <h2 className="home-hero-card-title">FORJA TU WARBAND.</h2>
              <h2 className="home-hero-card-title home-hero-card-title--cyan">DOMINA RASGANORTE.</h2>
              <p className="home-hero-card-sub">
                WotLK reinventado para jugar solo o en cooperativo,{' '}
                con <span className="cyan-highlight">Warbands</span>,{' '}
                <span className="cyan-highlight">Mythic+</span> y{' '}
                <span className="cyan-highlight">progresión flexible</span>.
              </p>
              <a href="/descargas" className="cta-button home-hero-card-btn" data-testid="cta-play-now" style={{ gap: 8 }}>
                <Swords size={15} />
                JUGAR AHORA
              </a>
            </div>
          </div>
          <div className="home-hero-card-features">
            <div className="home-hero-card-feature">
              <Users size={20} className="home-hero-card-feature-icon" />
              <div>
                <div className="home-hero-card-feature-title">Tu Propia Warband</div>
                <div className="home-hero-card-feature-desc">Controla tanques, sanadores y DPS personalizados.</div>
              </div>
            </div>
            <div className="home-hero-card-feature">
              <Hourglass size={20} className="home-hero-card-feature-icon" />
              <div>
                <div className="home-hero-card-feature-title">Progresión Flexible</div>
                <div className="home-hero-card-feature-desc">Elige tu ritmo con experiencia ajustable y acceso rápido al endgame.</div>
              </div>
            </div>
            <div className="home-hero-card-feature">
              <Swords size={20} className="home-hero-card-feature-icon" />
              <div>
                <div className="home-hero-card-feature-title">Desafío Cooperativo</div>
                <div className="home-hero-card-feature-desc">Supera Mythic+, raids y contenido escalable con tu Warband.</div>
              </div>
            </div>
          </div>
        </div>
        {/* Personaje libre: absoluto respecto a la sección, sin caja contenedora */}
        <img src="/frostbound-pj.png" alt="" aria-hidden="true" className="home-hero-pj" />
      </section>

      {/* ── PRIMEROS PASOS BAND ── */}
      <section className="home-guides-band anim-fade-up" style={{ animationDelay: '0.6s' }}>
        <div className="home-guides-band-card" data-testid="home-guides-band">
          <div className="home-guides-band-content">
            <div className="home-guides-band-eyebrow">
              <Clapperboard size={14} />
              <span>EMPIEZA TU AVENTURA EN RASGANORTE</span>
            </div>
            <h2 className="home-guides-band-title">PRIMEROS PASOS</h2>
            <p className="home-guides-band-desc">
              ¿Nuevo en Frostbound? Descubre nuestras guías en vídeo y aprende todo lo necesario
              para invocar tu Warband, dominar el combate y comenzar la aventura.
            </p>
            <Link to="/primeros-pasos" className="home-guides-band-cta">
              VER GUÍAS DE INICIO
              <span className="home-guides-band-cta-arrow"><ArrowRight size={14} /></span>
            </Link>
          </div>
          <div className="home-guides-band-thumb">
            <Link to="/primeros-pasos" className="home-guides-band-thumb-link" aria-label="Ver guías de inicio">
              <img
                src="/guides-thumbnails/featured.jpg"
                alt="Guías de inicio Frostbound"
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.style.opacity = '0.25'; }}
              />
            </Link>
            <button
              type="button"
              className="home-guides-band-play"
              aria-label="Ver vídeo"
              onClick={() => setFeaturedVideoOpen(true)}
            >
              <Play size={26} fill="currentColor" />
            </button>
          </div>
        </div>
      </section>

      {/* ── FEATURED VIDEO MODAL ── */}
      {featuredVideoOpen && (
        <VideoModal guide={getFeaturedGuide()} onClose={() => setFeaturedVideoOpen(false)} />
      )}

      {/* ── NOTICIAS DESTACADAS ── */}
      <section className="home-news-section anim-fade-up" style={{ animationDelay: '0.8s' }}>
        <div className="home-news-section-inner">
          <div className="home-news-section-header">
            <Newspaper size={16} className="home-news-section-icon" />
            <span>NOTICIAS DESTACADAS</span>
            <Link to="/noticias" className="home-info-link" style={{ marginLeft: 'auto' }}>
              Ver todas <ArrowRight size={10} />
            </Link>
          </div>

          {news.length === 0 ? (
            <div className="home-news-empty">No hay noticias disponibles.</div>
          ) : (() => {
            const railCount = Math.min(8, news.length);
            const safeIdx = Math.min(activeNewsIdx, railCount - 1);
            const active = news[safeIdx];
            const activeCfg = CATEGORY_COLORS[active.category] || CATEGORY_COLORS.AVISO;
            const activeCover = getCoverUrl(active);
            const activeDest = active.slug ? `/noticias/${active.slug}` : '/noticias';
            return (
              <div className="home-news-v2-grid">
                {/* HERO */}
                <Link to={activeDest} className="home-news-v2-hero" data-testid="home-news-hero" key={active._id || active.slug}>
                  <div className="home-news-v2-hero-cover">
                    <img
                      src={activeCover}
                      alt={active.title}
                      onError={e => { if (e.target.src.includes('/news-fallbacks/')) { e.target.style.display = 'none'; } else { e.target.src = '/news-fallbacks/default.jpg'; } }}
                    />
                  </div>
                  <div className="home-news-v2-hero-grad" />
                  <div className="home-news-v2-hero-body">
                    <div className="home-news-v2-hero-top">
                      <span
                        className="news-category-badge"
                        style={{ background: activeCfg.bg, border: `1px solid ${activeCfg.border}`, color: activeCfg.text }}
                      >
                        {active.category}
                      </span>
                      <span className="home-news-v2-hero-date">{active.published_at}</span>
                    </div>
                    <h3 className="home-news-v2-hero-title">{active.title}</h3>
                    <p className="home-news-v2-hero-excerpt">{active.excerpt}</p>
                    <span className="home-news-v2-hero-cta">Leer artículo <ArrowRight size={12} /></span>
                  </div>
                </Link>

                {/* RAIL */}
                <div className="home-news-v2-rail-wrap">
                  <div className="home-news-v2-rail">
                    {news.slice(0, railCount).map((item, i) => {
                      const cfg = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.AVISO;
                      const cover = getCoverUrl(item);
                      const dest = item.slug ? `/noticias/${item.slug}` : '/noticias';
                      const isActive = i === safeIdx;
                      return (
                        <Link
                          to={dest}
                          key={item._id || item.slug}
                          className={`home-news-v2-item${isActive ? ' is-active' : ''}`}
                          onMouseEnter={isTouch ? undefined : () => setActiveNewsIdx(i)}
                          onFocus={() => setActiveNewsIdx(i)}
                          onClick={(e) => handleRailItemClick(e, i)}
                          data-testid={`home-news-card-${i}`}
                        >
                          <div className="home-news-v2-thumb">
                            <img
                              src={cover}
                              alt={item.title}
                              onError={e => { if (e.target.src.includes('/news-fallbacks/')) { e.target.style.display = 'none'; } else { e.target.src = '/news-fallbacks/default.jpg'; } }}
                            />
                          </div>
                          <div className="home-news-v2-item-body">
                            <div className="home-news-v2-item-meta">
                              <span
                                className="news-category-badge"
                                style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text }}
                              >
                                {item.category}
                              </span>
                              <span className="home-news-v2-item-date">{item.published_at}</span>
                            </div>
                            <h4 className="home-news-v2-item-title">{item.title}</h4>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── INFO ROW ── */}
      <div className="home-info-row anim-fade-up" style={{ animationDelay: '1s' }}>

        {/* TOP 5 JUGADORES — mismo formato tabla que /armeria sin columna CLASE */}
        <div className="home-info-card home-info-card--wide" data-testid="home-armory-preview">
          <div className="home-info-header">
            <span className="home-info-title">TOP JUGADORES</span>
            <Link to="/armeria" className="home-info-link" data-testid="armory-preview-link">
              Ver todos <ArrowRight size={10} />
            </Link>
          </div>
          <div className="armory-table-wrap armory-table-wrap--home">
            {characters.length === 0 ? (
              <div className="armory-loading">Invocando héroes...</div>
            ) : (
              <table className="armory-table" data-testid="home-armory-table">
                <thead>
                  <tr>
                    <th className="armory-th armory-th--rank">#</th>
                    <th className="armory-th">PERSONAJE</th>
                    <th className="armory-th armory-th--hide-md">RAZA</th>
                    <th className="armory-th armory-th--hide-md">HERMANDAD</th>
                    <th className="armory-th">EQUIPO (GS)</th>
                  </tr>
                </thead>
                <tbody>
                  {characters.slice(0, 5).map((char, i) => (
                    <tr
                      key={char.name}
                      className={`armory-row ${i % 2 === 0 ? 'armory-row--even' : ''}`}
                      data-testid={`preview-char-${i}`}
                    >
                      <td className="armory-td armory-td--rank">
                        {i === 0 && <Trophy size={14} style={{ color: '#FFD700', marginRight: 4 }} />}
                        {i === 1 && <Trophy size={14} style={{ color: '#C0C0C0', marginRight: 4 }} />}
                        {i === 2 && <Trophy size={14} style={{ color: '#CD7F32', marginRight: 4 }} />}
                        {i > 2 && <span style={{ color: 'rgba(255,255,255,0.35)' }}>{i + 1}</span>}
                      </td>
                      <td className="armory-td">
                        <button
                          className="char-name armory-char-link"
                          style={{ color: char.class_color, textShadow: `0 0 10px ${char.class_color}55`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                          onClick={() => navigate(`/armeria/${char.name}`)}
                        >
                          <ClassIcon className={char.class_name} size={16} />
                          {char.name}
                        </button>
                      </td>
                      <td className="armory-td armory-td--hide-md armory-td--muted">{char.race}</td>
                      <td className="armory-td armory-td--hide-md armory-td--muted">{char.guild || 'Sin hermandad'}</td>
                      <td className="armory-td">
                        <GearBar score={char.gear_score ?? 0} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* DISCORD & SERVER PANELS */}
        <div className="home-panels-col">

          {/* DISCORD PANEL */}
          <div className="home-panel-card home-panel-card--discord" data-testid="home-discord-card">
            <div className="home-panel-bg" style={{ backgroundImage: 'url(/discord-bg.jpg)' }} />
            <div className="home-panel-overlay home-panel-overlay--discord" />
            <div className="home-panel-content">
              <div className="home-panel-header">
                <svg className="home-panel-discord-icon" viewBox="0 0 24 24" fill="currentColor" width={18} height={18} aria-hidden="true">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.044.033.055a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                </svg>
                <span className="home-panel-title">ÚNETE A DISCORD</span>
              </div>
              <p className="home-panel-desc">
                Conéctate con la comunidad, participa en eventos y mantente al día de todo lo que sucede en Frostbound.
              </p>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="home-panel-btn home-panel-btn--discord"
              >
                UNIRSE AL DISCORD <ArrowRight size={12} />
              </a>
            </div>
          </div>

          {/* SERVER PANEL */}
          <div className="home-panel-card home-panel-card--server" data-testid="home-server-card">
            <div className="home-panel-bg" style={{ backgroundImage: 'url(/server-bg.jpg)' }} />
            <div className="home-panel-overlay home-panel-overlay--server" />
            <div className="home-panel-content">
              <div className="home-panel-header">
                <Wifi size={14} className="home-panel-wifi-icon" />
                <span className="home-panel-title">CONECTAR AL SERVIDOR</span>
              </div>
              <div className="home-server-info-grid">
                <div className="home-server-info-item">
                  <span className="home-server-info-label">Versión</span>
                  <span className="home-server-info-value">3.3.5a (WotLK)</span>
                </div>
                <div className="home-server-info-item">
                  <span className="home-server-info-label">EXP</span>
                  <span className="home-server-info-value">x1–7</span>
                </div>
                <div className="home-server-info-item">
                  <span className="home-server-info-label">Botín</span>
                  <span className="home-server-info-value">x2</span>
                </div>
                <div className="home-server-info-item">
                  <span className="home-server-info-label">Oro</span>
                  <span className="home-server-info-value">x2</span>
                </div>
                {online !== null && online > 0 && (
                  <div className="home-server-info-item home-server-info-item--online">
                    <span className="online-dot" />
                    <span className="home-server-info-value">{online.toLocaleString()} online</span>
                  </div>
                )}
              </div>
              <div className="home-server-rl-row">
                <code className="realmlist-code home-server-rl-code">{REALMLIST}</code>
                <div className="home-server-actions">
                  <button className="copy-btn" onClick={copyRealmlist} data-testid="home-copy-realmlist">
                    {copied ? <><CheckCircle size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                  </button>
                  <a href="/descargas" className="home-panel-btn home-panel-btn--download">
                    <Download size={11} /> DESCARGAR CLIENTE
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer fixed />
    </PageWrapper>
  );
}
